import { ThemeEvents } from '@theme/events';

/**
 * Presents a quantity selector in meters for fabric sold per 0.5 m.
 *
 * The wrapped quantity selector keeps working in units (1 unit = `data-unit-length` meters),
 * so add to cart, inventory and cart updates stay native. This element only:
 * - shows the selected length (e.g. "2.5 m") in place of the unit count,
 * - offers quick length buttons that set the unit quantity,
 * - shows the running total, and
 * - keeps the `properties[Length]` input in sync for checkout and orders.
 *
 * It is a plain HTMLElement (not a `-component`) on purpose: wrapping the quantity selector
 * in a Component would take ownership of refs that the product form and cart rely on.
 */
class MeterPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
  }

  get unitLength() {
    return Number(this.dataset.unitLength) || 0.5;
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[ref="quantityInput"], input[ref="quantityInputs[]"], .quantity-selector input[type="number"]'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const lengthButton = target.closest('[data-meter-length]');

    if (lengthButton instanceof HTMLElement) {
      event.preventDefault();
      this.#setUnits(Math.round(Number(lengthButton.dataset.meterLength) / this.unitLength));
      return;
    }

    // Plus/minus are handled by the quantity selector; read the new value once it has applied.
    if (target.closest('button[name="plus"], button[name="minus"]')) {
      requestAnimationFrame(() => this.#render());
    }
  };

  #onFocusOut = () => requestAnimationFrame(() => this.#render());

  #onQuantityUpdate = () => this.#render();

  /** @param {number} units */
  #setUnits(units) {
    const input = this.#input;
    const selector = /** @type {any} */ (input?.closest('quantity-selector-component, cart-quantity-selector-component'));
    if (!input || !selector) return;

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const clamped = Math.max(min, Math.min(max, units));

    input.value = String(clamped);
    selector.onQuantityChange?.();
    selector.updateButtonStates?.();
    this.#render();
  }

  #render() {
    const input = this.#input;
    if (!input) return;

    const units = Number(input.value) || 0;
    const meters = units * this.unitLength;
    const label = `${formatMeters(meters)} ${this.dataset.unitLabel ?? 'm'}`;

    const display = this.querySelector('[data-meter-display]');
    if (display) display.textContent = label;

    const lengthInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-meter-property]'));
    if (lengthInput) lengthInput.value = label;

    for (const button of this.querySelectorAll('[data-meter-length]')) {
      const active = Number(/** @type {HTMLElement} */ (button).dataset.meterLength) === meters;
      button.setAttribute('aria-pressed', String(active));
    }

    const total = this.querySelector('[data-meter-total]');
    const unitPrice = Number(this.dataset.unitPrice);
    if (total && Number.isFinite(unitPrice)) {
      total.textContent = formatMoney(unitPrice * units, this.dataset.moneyFormat ?? '');
    }
  }
}

/** @param {number} meters */
function formatMeters(meters) {
  return Number.isInteger(meters) ? String(meters) : meters.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Formats cents with the shop's money format. Supports the format placeholders Horizon stores use.
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const amount = cents / 100;
  const withCommas = (/** @type {number} */ value, /** @type {number} */ decimals) =>
    value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const placeholders = {
    amount: withCommas(amount, 2),
    amount_no_decimals: withCommas(Math.round(amount), 0),
    amount_with_comma_separator: withCommas(amount, 2).replace(/,/g, '_').replace('.', ',').replace(/_/g, '.'),
    amount_no_decimals_with_comma_separator: withCommas(Math.round(amount), 0).replace(/,/g, '.'),
    amount_with_apostrophe_separator: withCommas(amount, 2).replace(/,/g, "'"),
  };

  if (!format) return placeholders.amount;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => placeholders[/** @type {keyof typeof placeholders} */ (key)] ?? placeholders.amount);
}

if (!customElements.get('meter-picker')) {
  customElements.define('meter-picker', MeterPicker);
}
